import { useState } from 'react';
import { toast } from 'sonner';
import { useLoaderData, useRevalidator } from 'react-router-dom';

import { CycleDayStepper } from '../../components/onboarding/CycleDayStepper';
import { SettingsSubPageLayout } from '../../components/settings/SettingsSubPageLayout';
import { Button } from '../../components/ui/Button';
import { authAPI } from '../../data/auth';
import { settingsHubLoader } from '../../data/settings';
import pt from '../../locales/pt';
import { useAuth } from '../../store/AuthContext';
import { clampSelfieDay } from '../../utils/guidedFlowUtils';
import { projectMonthlyCyclePeriod } from '../../utils/selfiePeriod';

export function CycleDaySettings() {
  const { user } = useLoaderData();
  const { refreshMe } = useAuth();
  const revalidator = useRevalidator();
  const savedDay = clampSelfieDay(user?.profile?.selfie_day_of_month ?? 1);
  const [day, setDay] = useState(() => savedDay);
  const [loading, setLoading] = useState(false);

  const currentPeriod = projectMonthlyCyclePeriod(savedDay);
  const previewPeriod = projectMonthlyCyclePeriod(day);
  const dayChanged = day !== savedDay;

  const onSave = async () => {
    setLoading(true);
    const response = await authAPI.patchProfile({
      selfie_day_of_month: clampSelfieDay(day),
    });
    setLoading(false);
    if (!response.success) {
      toast.error(response.message);
      return;
    }
    await refreshMe();
    revalidator.revalidate();
    toast.success(pt.settings.cycleDay.saved);
  };

  return (
    <SettingsSubPageLayout
      section={pt.settings.cycleDay.section}
      title={pt.settings.cycleDay.title}
      subtitle={pt.settings.cycleDay.subtitle}
      footer={
        <Button type="button" className="w-full" disabled={loading} onClick={onSave}>
          {pt.settings.cycleDay.submit}
        </Button>
      }
    >
      <p className="rounded-2xl border border-border bg-muted/40 px-4 py-3 text-sm leading-relaxed text-foreground">
        {pt.settings.cycleDay.currentPeriod
          .replace('{start}', formatDatePart(currentPeriod.start))
          .replace('{end}', formatDatePart(currentPeriod.end))}
      </p>

      <CycleDayStepper value={day} onChange={setDay} className="rounded-2xl border border-border bg-card" />

      {dayChanged ? (
        <div className="flex gap-2.5 rounded-2xl border border-amber-200 bg-amber-50 p-3.5 dark:border-amber-900 dark:bg-amber-950/30">
          <p className="text-xs leading-relaxed text-amber-900 dark:text-amber-100">
            {pt.settings.cycleDay.previewPeriod
              .replace('{day}', String(day))
              .replace('{start}', formatDatePart(previewPeriod.start))
              .replace('{end}', formatDatePart(previewPeriod.end))}
          </p>
        </div>
      ) : null}

      <p className="text-xs text-muted-foreground">{pt.settings.cycleDay.hint}</p>
    </SettingsSubPageLayout>
  );
}

function formatDatePart(iso) {
  if (!iso) return '';
  const date = new Date(`${iso}T12:00:00`);
  return new Intl.DateTimeFormat('pt-PT', { day: 'numeric', month: 'long' }).format(date);
}

CycleDaySettings.loader = settingsHubLoader;
